import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from './api';

interface GoogleListing {
  accountId: string;
  accountName: string;
  locationId: string;
  title: string;
  address: string | null;
  phone: string | null;
  category: string | null;
  linkedRestaurantId: number | null;
  linkedRestaurantName: string | null;
  linkedElsewhere: boolean;
}

// Error codes from the G2 routes and src/google.ts, each with its own
// translated message (the server's own text is English only).
const KNOWN_ERRORS = [
  'not_your_listing', 'linked_elsewhere', 'linked_to_own', 'reconnect_required',
  'google_rate_limited', 'google_forbidden', 'google_not_found', 'google_unavailable',
];

interface LinkableRestaurant {
  id: number;
  name: string;
  address?: string | null;
  phone?: string | null;
  cuisine?: string | null;
  googleLocationId?: string | null;
  googleLocationTitle?: string | null;
}

/**
 * G2 of GOOGLE_SYNC_PLAN.md: which Google Business Profile listing feeds this
 * restaurant. Shown inside the Restaurant page's Google section once the
 * owner's Google account is connected. The server re-checks every link against
 * the owner's own Google access, so this list is a convenience, not the guard.
 */
const GoogleListingLink: React.FC<{
  restaurant: LinkableRestaurant;
  onChange: (updated: any) => void;
}> = ({ restaurant, onChange }) => {
  const { t } = useTranslation();
  const [listings, setListings] = useState<GoogleListing[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string>('');
  // Fred, 2026-10-13: show both sets of details and let the owner choose.
  // Unticked still fills empty fields; ticked overwrites with Google's.
  const [useGoogleDetails, setUseGoogleDetails] = useState(false);
  // Linked view: Google's current details for this restaurant's own listing.
  const [linkedListing, setLinkedListing] = useState<GoogleListing | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const errorFor = (data: any, fallbackKey: string) =>
    KNOWN_ERRORS.includes(data?.code) ? t(`restaurant.google.errors.${data.code}`) : t(fallbackKey);

  const loadListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/google/locations');
      const data = await res.json().catch(() => null);
      if (!res.ok || !Array.isArray(data)) {
        setError(errorFor(data, 'restaurant.google.listingsFailed'));
        return;
      }
      if (restaurant.googleLocationId) {
        const own = data.find((l: GoogleListing) => l.locationId === restaurant.googleLocationId);
        if (!own) setError(t('restaurant.google.errors.not_your_listing'));
        setLinkedListing(own || null);
        return;
      }
      setListings(data);
      const firstFree = data.find((l: GoogleListing) => !l.linkedElsewhere && !l.linkedRestaurantId);
      setSelected(firstFree ? firstFree.locationId : '');
    } catch {
      setError(t('restaurant.google.listingsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const link = async (choice: GoogleListing | undefined, replaceDetails: boolean) => {
    if (!choice) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant.id}/google/link`, {
        method: 'POST',
        body: JSON.stringify({ accountId: choice.accountId, locationId: choice.locationId, fillDetails: true, replaceDetails }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(errorFor(data, 'restaurant.google.linkFailed'));
        return;
      }
      setListings(null);
      setLinkedListing(null);
      setUseGoogleDetails(false);
      onChange(data);
    } catch {
      setError(t('restaurant.google.linkFailed'));
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant.id}/google/link`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(errorFor(data, 'restaurant.google.unlinkFailed'));
        return;
      }
      setConfirmUnlink(false);
      onChange(data);
    } catch {
      setError(t('restaurant.google.unlinkFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Side by side: what ReviewCounter has now vs what Google has.
  const comparison = (l: GoogleListing) => {
    const rows = [
      { label: t('restaurant.google.fieldName'), ours: restaurant.name, theirs: l.title },
      { label: t('restaurant.google.fieldAddress'), ours: restaurant.address, theirs: l.address },
      { label: t('restaurant.google.fieldPhone'), ours: restaurant.phone, theirs: l.phone },
      { label: t('restaurant.google.fieldCuisine'), ours: restaurant.cuisine, theirs: l.category },
    ];
    return (
      <table className="w-full text-sm border border-gray-200 rounded">
        <thead className="bg-gray-50 text-left text-gray-600">
          <tr>
            <th className="p-2 font-medium"></th>
            <th className="p-2 font-medium">{t('restaurant.google.colReviewCounter')}</th>
            <th className="p-2 font-medium">{t('restaurant.google.colGoogle')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const differs = Boolean(r.theirs) && (r.ours || '').trim() !== (r.theirs || '').trim();
            return (
              <tr key={r.label} className="border-t border-gray-200 align-top">
                <td className="p-2 text-gray-600">{r.label}</td>
                <td className="p-2">{r.ours || <span className="text-gray-400">{t('restaurant.google.empty')}</span>}</td>
                <td className={`p-2 ${differs ? 'font-semibold text-teal-800' : ''}`}>
                  {r.theirs || <span className="text-gray-400">{t('restaurant.google.empty')}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (restaurant.googleLocationId) {
    return (
      <div className="border-t pt-4 space-y-3">
        <p className="text-sm text-gray-700">
          {t('restaurant.google.linkedTo', { restaurant: restaurant.name, listing: restaurant.googleLocationTitle || restaurant.googleLocationId })}
        </p>
        {error && <div className="p-3 rounded bg-red-50 text-red-800 text-sm">{error}</div>}
        {linkedListing ? (
          <div className="space-y-3">
            {comparison(linkedListing)}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => link(linkedListing, true)}
                disabled={saving}
                className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
              >
                {saving ? t('restaurant.google.saving') : t('restaurant.google.applyGoogleDetails')}
              </button>
              <button
                onClick={() => setLinkedListing(null)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
              >
                {t('restaurant.google.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={loadListings}
            disabled={loading}
            className="px-4 py-2 border border-teal-600 text-teal-700 rounded hover:bg-teal-50 disabled:opacity-50 text-sm mr-3"
          >
            {loading ? t('restaurant.google.loadingListings') : t('restaurant.google.compare')}
          </button>
        )}
        {!confirmUnlink ? (
          <button
            onClick={() => setConfirmUnlink(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
          >
            {t('restaurant.google.unlink')}
          </button>
        ) : (
          <div className="p-4 rounded border border-red-200 bg-red-50 space-y-3">
            <p className="text-sm text-red-800">{t('restaurant.google.unlinkWarning')}</p>
            <div className="flex gap-3">
              <button
                onClick={unlink}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {t('restaurant.google.confirmUnlink')}
              </button>
              <button
                onClick={() => setConfirmUnlink(false)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-white text-sm"
              >
                {t('restaurant.google.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-t pt-4 space-y-3">
      <p className="text-sm text-gray-700">{t('restaurant.google.notLinked', { restaurant: restaurant.name })}</p>
      {error && <div className="p-3 rounded bg-red-50 text-red-800 text-sm">{error}</div>}
      {listings === null ? (
        <button
          onClick={loadListings}
          disabled={loading}
          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
        >
          {loading ? t('restaurant.google.loadingListings') : t('restaurant.google.chooseListing')}
        </button>
      ) : listings.length === 0 ? (
        <p className="text-sm text-gray-600">{t('restaurant.google.noListings')}</p>
      ) : (
        <div className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="sr-only">{t('restaurant.google.chooseListing')}</legend>
            {listings.map((l) => {
              const taken = l.linkedElsewhere || Boolean(l.linkedRestaurantId);
              return (
                <label
                  key={l.locationId}
                  className={`flex items-start gap-3 p-3 rounded border ${taken ? 'border-gray-200 bg-gray-50 text-gray-400' : selected === l.locationId ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'} ${taken ? '' : 'cursor-pointer'}`}
                >
                  <input
                    type="radio"
                    name="google-listing"
                    className="mt-1"
                    disabled={taken}
                    checked={selected === l.locationId}
                    onChange={() => setSelected(l.locationId)}
                  />
                  <span className="text-sm">
                    <span className="font-semibold block">{l.title}</span>
                    {l.address && <span className="block">{l.address}</span>}
                    {l.phone && <span className="block">{l.phone}</span>}
                    {l.linkedRestaurantName && (
                      <span className="block italic">{t('restaurant.google.alreadyLinkedOwn', { name: l.linkedRestaurantName })}</span>
                    )}
                    {l.linkedElsewhere && <span className="block italic">{t('restaurant.google.alreadyLinkedElsewhere')}</span>}
                  </span>
                </label>
              );
            })}
          </fieldset>
          {listings.find((l) => l.locationId === selected) && (
            <div className="space-y-2">
              {comparison(listings.find((l) => l.locationId === selected) as GoogleListing)}
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" className="mt-1" checked={useGoogleDetails} onChange={(e) => setUseGoogleDetails(e.target.checked)} />
                <span>
                  {t('restaurant.google.useGoogleDetails')}
                  <span className="block text-gray-500">{t('restaurant.google.useGoogleDetailsHint')}</span>
                </span>
              </label>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => link(listings.find((l) => l.locationId === selected), useGoogleDetails)}
              disabled={saving || !selected}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
            >
              {saving ? t('restaurant.google.linking') : t('restaurant.google.link')}
            </button>
            <button
              onClick={() => setListings(null)}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
            >
              {t('restaurant.google.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleListingLink;
